import React from "react";
import { Modal } from "Components/General/Modal/GenericModal";
import { I18N_NS } from "Config/i18n";
import { Input, Empty, Alert, Row } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { Button } from "Components/General/Button";
import { ConventionChecker } from "./ConventionChecker";

interface ValidValuesPickerProps {
  isVisible: boolean;
  data: {
    values: { name: string; description: string }[];
    initialValue: string;
    activePartConventions: API_FilenameAssistantConventionResponse[];
  };
  title: string;
  onCancel: () => void;
  onChange: (val: string) => void;
  multiConventionCase?: "AND" | "OR";
  allowAsterics: boolean;
  hideButtons?: boolean;
  onLocalChange?: (value: string) => void;
  onValidityChange?: (isValid: boolean) => void;
}

export const ValidValuesPicker: React.FC<Partial<ValidValuesPickerProps>> = ({
  data,
  onChange,
  onCancel,
  multiConventionCase,
  allowAsterics,
  hideButtons,
  onLocalChange,
  onValidityChange,
}) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [filteredData, setData] = React.useState<{ name: string; description: string }[]>([]);
  const [inputValue, setValue] = React.useState<string>("");
  const [isValid, setValid] = React.useState<boolean>(false);
  const [searchValue, setSearchValue] = React.useState<string>("");
  const isInitializing = React.useRef<boolean>(false);
  // The cell value carries technical markers for parts that did not pass the convention check
  const cellValue = React.useMemo(
    () => (data.initialValue || "").replace("_invalidValue", "").replace("undefined", ""),
    [data.initialValue]
  );
  // Until the user picks or types something, the cell value itself is the value being edited
  const currentValue = inputValue || cellValue;

  const activeConvention = data.activePartConventions?.[0];
  const conventionName = activeConvention?.name || activeConvention?.metadata?.name;
  const conventionDescription = activeConvention?.description || activeConvention?.metadata?.description;
  const infoText = (value: string, isValidValue: boolean) =>
    !isValidValue ? (
      <div>
        <span className="mr-sm">{t("Currently wrong value")}:</span>
        <strong>{value}</strong>
        <div>
          {t(
            allowAsterics
              ? "Select metadata value or type in *"
              : "Please select the correct value within the list of allowed values below."
          )}
        </div>
      </div>
    ) : (
      <div>
        <span className="mr-sm">{t("Currently valid value")}:</span>
        <strong>{value}</strong>
        {allowAsterics && <div>{t("Select metadata value or type in *")}</div>}
      </div>
    );
  const handleFilter = React.useCallback(() => {
    const newData = data.values.filter((item) => item.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1);
    setData(newData);
  }, [searchValue, data]);

  const handleSave = () => {
    const valueToSave = currentValue;
    if (onLocalChange) {
      onLocalChange(valueToSave);
    } else {
      onChange(valueToSave);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onChange(data.initialValue);
    }
  };

  const isValidValue = React.useCallback(() => {
    setValid(
      (allowAsterics && currentValue === "*") ||
        (data.activePartConventions?.length > 0 && ConventionChecker.check(currentValue, data.activePartConventions))
    );
  }, [currentValue, data, allowAsterics]);

  React.useEffect(() => {
    handleFilter();
  }, [searchValue, handleFilter]);

  React.useEffect(() => {
    isValidValue();
  }, [isValidValue]);

  React.useEffect(() => {
    if (onLocalChange && currentValue && isValid && !isInitializing.current) {
      onLocalChange(currentValue);
    }
  }, [currentValue, isValid]);

  React.useEffect(() => {
    if (data.values && data.values.length > 0) {
      setData(data.values);
    }
  }, [data.values]);

  React.useEffect(() => {
    if (onValidityChange) {
      onValidityChange(isValid);
    }
  }, [isValid]);

  const handleUserInput = (value: string) => {
    isInitializing.current = false;
    setSearchValue(value);
    setValue(value);
  };

  const handleUserSelect = (value: string) => {
    isInitializing.current = false;
    setValue(value);
    setSearchValue(value);
  };

  return (
    <>
      {(conventionName || conventionDescription) && (
        <div className="mb-2">
          {conventionName && <strong>{conventionName}</strong>}
          {conventionDescription && <div>{conventionDescription}</div>}
        </div>
      )}
      {currentValue && (
        <div className="mb-md" style={{ display: "block" }}>
          <Alert
            title={infoText(currentValue, isValid)}
            type="info"
            showIcon
            icon={<FontAwesomeIcon icon={["far", "info-circle"]} />}
          />
        </div>
      )}
      <Input
        onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => handleUserInput(value)}
        placeholder={t("Search in values")}
        className="mb-2"
        value={searchValue}
      />
      <div style={{ height: 220, overflow: "scroll" }} className="mb-2">
        <Row style={{ width: "100%" }} align="middle" wrap justify="start">
          {filteredData.map(({ name, description }, index) => (
            <div
              key={`${name}_${index}`}
              style={{
                width: "calc((100% / 3 ) - 8px)",
                height: 42,
                overflow: "hidden",
                border: "1px solid #e4e4e4",
                borderRadius: "4px",
                margin: 4,
                padding: 12,
                display: "flex",
                alignItems: "center",
              }}
              className="text-ellipsis"
              onClick={() => handleUserSelect(name)}
            >
              <strong>{name}</strong>
              {description && `- ${description}`}
            </div>
          ))}
          {!filteredData.length && (
            <Empty style={{ width: "100%" }} image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("No valid values")} />
          )}
        </Row>
      </div>
      {!hideButtons && (
        <Row justify="space-between" className="mt-md">
          <Button onClick={handleCancel}>{multiConventionCase ? t("Back") : t("Cancel")}</Button>
          <Button type="primary" onClick={handleSave} disabled={!isValid}>
            {t("Save")}
          </Button>
        </Row>
      )}
    </>
  );
};

export const MultiValuesPickerModal: React.FC<ValidValuesPickerProps> = ({
  title,
  isVisible,
  onCancel,
  onChange,
  data,
  allowAsterics,
}) => {
  return (
    <Modal
      maskClosable={false}
      destroyOnHidden={true}
      title={title}
      open={isVisible}
      width={"100%"}
      style={{ maxWidth: 900, top: 40 }}
      zIndex={1012}
      closable={false}
      footer={null}
    >
      <ValidValuesPicker data={data} onChange={onChange} onCancel={onCancel} allowAsterics={allowAsterics} />
    </Modal>
  );
};
